/* ============================================================
   useSpecialTabs — the pinned Welcome.md / Docs.md documents

   These are real documents that render through the normal DocView,
   but they're EPHEMERAL: never written to IndexedDB, never shown in
   the recent list (DocView gets `ephemeral`, and the reader keeps
   them out of the `tabs` array). They behave like any other doc —
   you can select text, comments work, scroll is live.

   Welcome.md ships with a couple of real demo comments, anchored by
   computing each quote's character offset within the rendered prose
   (the same offset model DocView persists), so they highlight + show
   margin cards exactly like a reviewer's own comments — no hand-typed
   DOM offsets, no special-cased fake.

   A future option is to ship the Welcome as a real published plan decoded
   through the normal crypto path instead of seeding comments here.
   ============================================================ */

import type { Tab, Comment } from '@/types'
import { renderMarkdownWithLineInfo } from './useMarkdown'

/** Sentinel hashes — distinct from any real 64-hex SHA-256, so these
 *  never collide with or get treated as persisted documents. */
export const WELCOME_HASH = '__welcome__'
export const DOCS_HASH = '__docs__'

// First-run welcome copy: the "why this exists" story + the hands-on demo.
// NOTE: demo-comment quotes are matched against the RENDERED text, and the
// renderer runs markdown-it with typographer on — keep WELCOME_DEMO quotes
// free of apostrophes/straight quotes/markup, and keep their source sentences
// in sync if you edit the prose below.
const WELCOME_MD = [
  '# ![Redline](/favicon.svg) Welcome to Redline!',
  '',
  'Redline is a **free, public-good service** for reviewing the plans your coding agents write. No accounts, no paid tier, no "free until we get acquired" — it is free, it stays free, and it exists because squinting at a wall of text in a terminal is no way to review anything.',
  '',
  'You know the loop: Claude Code (or Codex, or Gemini…) produces a two-thousand-line plan, you skim it in a pager, type some feedback into chat, and hope. Redline gives that plan a real reading surface instead — rendered markdown, comments anchored to the exact lines you highlight, and a clean handoff back to your agent when you are done.',
  '',
  '## Try it right now',
  '',
  'This page is a real document. Select any text and a comment card appears in the margin — that is the whole reviewing motion. We left a couple of comments here already so you can see how they land.',
  '',
  '## The loop',
  '',
  '1. **Connect your agent.** Hit the ⚡ **Install** button in the tab bar, pick your agent, and paste the prompt it gives you. From then on your agent knows how to share plans here on its own.',
  '2. **Your agent shares a plan.** It encrypts the plan locally and hands you a link.',
  '3. **You review.** Read it like a document, highlight anything, say what you think.',
  '4. **Send feedback.** Hit **Send feedback** (or **⌘K** / **Ctrl+K**) to copy your comments as one tidy, formatted response your agent can act on.',
  '',
  'No agent handy? Drop a markdown file anywhere on this window, or paste one with the **+** menu — the reader works just as well on plans you bring yourself.',
  '',
  '## Private by construction',
  '',
  'No sign-up and no visitor or document tracking. Redline records a few **anonymous daily counters**—for example, that a plan opened or feedback was copied—but never stores an IP, identity, document slug, URL, or content with those events. Plans are encrypted with **AES-256-GCM before they are uploaded**, and the decryption key travels in the URL fragment—the part after the `#` that your browser never sends to any server. We store ciphertext we cannot read; that is the whole arrangement.',
  '',
  'Redline is open source (MIT), and there is no business model waiting in the wings — your plans are not data to us, they are ciphertext. Public goods should just work, and this one intends to.',
  '',
  'Want the fine print — the crypto contract, the API, recipes for every language? It all lives in the **Docs** tab on the right.',
].join('\n')

/** A demo comment: a quote to find in the prose + the note to attach. */
interface DemoComment {
  quote: string
  note: string
}

// Demo comments. Each `quote` must appear verbatim in the RENDERED prose
// (post-typographer, markup stripped) — see the note above WELCOME_MD.
const WELCOME_DEMO: DemoComment[] = [
  {
    quote: 'Select any text',
    note: 'Like this! Comments anchor to exactly what you highlight. Try selecting the rest of this sentence and leaving one of your own.',
  },
  {
    quote: 'type some feedback into chat, and hope',
    note: "This is the part Redline replaces — your comments go back as structured, quoted feedback instead of a vague paragraph the agent has to guess about.",
  },
  {
    quote: 'ciphertext we cannot read',
    note: 'Zero-knowledge by construction: encryption happens before upload, and the key never leaves your URL bar. Lose the link, lose the plan — there is no recovery, by design.',
  },
]

/**
 * Anchor demo comments by locating each quote's character offset within the
 * rendered prose's text content — the same offset basis DocView persists in
 * `domRangeStart/End`. Runs in the browser (needs a DOM to measure).
 */
function seedComments(markdown: string, demos: DemoComment[]): Comment[] {
  const probe = document.createElement('div')
  probe.innerHTML = renderMarkdownWithLineInfo(markdown)
  const text = probe.textContent || ''
  const now = Date.now()
  const out: Comment[] = []
  for (const d of demos) {
    const idx = text.indexOf(d.quote)
    if (idx === -1) continue // quote not found → skip rather than mis-anchor
    out.push({
      id: crypto.randomUUID(),
      // sourceLine/blockHint only feed the handoff payload; the highlight +
      // card rehydrate purely from the dom offsets below.
      sourceLineStart: 0,
      sourceLineEnd: 0,
      quotedText: d.quote,
      blockHint: '',
      domRangeStart: idx,
      domRangeEnd: idx + d.quote.length,
      note: d.note,
      createdAt: now,
    })
  }
  return out
}

function makeTab(hash: string, filename: string, markdown: string, comments: Comment[]): Tab {
  const now = Date.now()
  return {
    hash,
    shortHash: hash.slice(2, 6),
    filename,
    markdown,
    comments,
    generalNotes: '',
    summary: '',
    scrollTop: 0,
    activeCommentId: null,
    createdAt: now,
    updatedAt: now,
  }
}

/** Build the ephemeral first-run Welcome.md tab (with demo comments). */
export function buildWelcomeTab(): Tab {
  return makeTab(WELCOME_HASH, 'Welcome.md', WELCOME_MD, seedComments(WELCOME_MD, WELCOME_DEMO))
}

/** Build the ephemeral Docs.md tab from already-fetched docs markdown. */
export function buildDocsTab(markdown: string): Tab {
  return makeTab(DOCS_HASH, 'Docs.md', markdown, [])
}

export function useSpecialTabs() {
  return { buildWelcomeTab, buildDocsTab, WELCOME_HASH, DOCS_HASH }
}
